import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Avatar,
	Box,
	Card,
	CardContent,
	Chip,
	Container,
	FormControl,
	Grid,
	IconButton,
	Input,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material';
import { DatePicker, message } from 'antd';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import FilterListIcon from '@mui/icons-material/FilterList';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CakeIcon from '@mui/icons-material/Cake';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ListAltIcon from '@mui/icons-material/ListAlt';

const { RangePicker } = DatePicker;

const formatDate = (dateString) => {
	if (!dateString) return 'Fecha no disponible';
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return 'Fecha no disponible';
	return format(date, 'dd/MM/yyyy hh:mm a', { locale: es });
};

const normalizeEventTypeLabel = (eventType) => {
	if (!eventType) return 'Evento';
	if (eventType === 'rental') return 'Renta';
	if (eventType === 'outside') return 'Externo';
	return 'Interno';
};

const normalizePaymentStatus = (status) => {
	if (!status) return 'pendiente';
	return status.toString().toLowerCase();
};

const formatCurrency = (value) => {
	const parsed = Number.parseFloat((value || '0').toString().replace(/,/g, ''));
	const amount = Number.isNaN(parsed) ? 0 : parsed;
	return amount.toLocaleString('es-DO', {
		style: 'currency',
		currency: 'DOP',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	});
};

function ViewEventsList({ token }) {
	const [eventsList, setEventsList] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [eventType, setEventType] = useState('all');
	const [paymentStatus, setPaymentStatus] = useState('all');
	const [dates, setDates] = useState(null);
	const [messageApi, contextHolder] = message.useMessage();

	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const fetchEvents = async () => {
		setIsLoading(true);
		const accessToken = token || localStorage.getItem('token');

		try {
			const response = await fetch('https://dprincess-training.servehttp.com/events/events-details', {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${accessToken}`
				}
			});

			if (!response.ok) {
				throw new Error('No se pudieron obtener los eventos.');
			}

			const data = await response.json();
      console.log("Lista de eventos: ", data);
      
			setEventsList(Array.isArray(data) ? data : []);
		} catch (error) {
			messageApi.error(error.message || 'Error cargando eventos.');
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchEvents();
	}, []);

	const filteredEvents = useMemo(() => {
		const text = search.trim().toLowerCase();

		return eventsList.filter((item) => {
			const event = item?.event || {};
			const client = item?.client || {};
			const xv = item?.XV;
			const weddingpartner = item?.weddingpartner;
			const birthdaydude = item?.birthdaydude;

			const matchesSearch =
				!text ||
				(client.clientName || '').toLowerCase().includes(text) ||
				(event.eventTitle || '').toLowerCase().includes(text) ||
				(xv?.XVName || '').toLowerCase().includes(text) ||
				(weddingpartner?.name || '').toLowerCase().includes(text) ||
				(birthdaydude?.name || '').toLowerCase().includes(text);

			const matchesType =
				eventType === 'all' ||
				(eventType === 'xv' && !!xv) ||
				(eventType === 'wedding' && !!weddingpartner) ||
				(eventType === 'birthday' && !!birthdaydude) ||
				(eventType === 'rental' && event.eventType === 'rental');

			const normalizedStatus = normalizePaymentStatus(event.paymentStatus || item?.invoice?.paymentStatus);
			const matchesPayment = paymentStatus === 'all' || normalizedStatus === paymentStatus;

			let matchesDates = true;
			if (dates && dates.length === 2) {
				const start = dates[0]?.valueOf?.();
				const end = dates[1]?.valueOf?.();
				const eventStart = new Date(event.eventDateStart).getTime();
				const eventEnd = new Date(event.eventDateEnd).getTime();
				matchesDates = !!start && !!end && eventStart >= start && eventEnd <= end;
			}

			return matchesSearch && matchesType && matchesPayment && matchesDates;
		});
	}, [eventsList, search, eventType, paymentStatus, dates]);

	const getEventProfile = (item) => {
		if (item?.weddingpartner) {
			return {
				title: 'Pareja',
				value: item.weddingpartner.name || 'Sin nombre',
				icon: <FavoriteIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />,
				color: 'secondary'
			};
		}

		if (item?.XV) {
			return {
				title: 'Quinceañera',
				value: item.XV.XVName || 'Sin nombre',
				icon: <CakeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />,
				color: 'warning'
			};
		}

		if (item?.birthdaydude) {
			return {
				title: 'Cumpleañero/a',
				value: item.birthdaydude.name || 'Sin nombre',
				icon: <CelebrationIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />,
				color: 'success'
			};
		}

		return {
			title: 'Perfil',
			value: 'Evento general',
			icon: <EventIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />,
			color: 'default'
		};
	};

	return (
		<Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
			{contextHolder}

			<Typography
				variant={isMobile ? 'h5' : 'h4'}
				sx={{
					fontWeight: 700,
					color: '#1976d2',
					backgroundColor: '#fff',
					borderRadius: 2,
					px: 1,
					py: 2,
					mb: 3,
					textAlign: 'center'
				}}
			>
				Lista de eventos
			</Typography>

			<Card sx={{ mb: 3, borderRadius: 2 }}>
				<CardContent>
					<Grid container spacing={2.2} alignItems="flex-end">
						<Grid item xs={12}>
							<Typography sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
								<FilterListIcon fontSize="small" /> Filtros
							</Typography>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth>
								<InputLabel htmlFor="search-event">Buscar</InputLabel>
								<Input
									id="search-event"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth>
								<InputLabel>Tipo</InputLabel>
								<Select value={eventType} onChange={(e) => setEventType(e.target.value)}>
									<MenuItem value="all">Todos</MenuItem>
									<MenuItem value="xv">Quinceaños</MenuItem>
									<MenuItem value="wedding">Bodas</MenuItem>
									<MenuItem value="birthday">Cumpleaños</MenuItem>
									<MenuItem value="rental">Rentas</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth>
								<InputLabel>Estado de pago</InputLabel>
								<Select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
									<MenuItem value="all">Todos</MenuItem>
									<MenuItem value="pendiente">Pendiente</MenuItem>
									<MenuItem value="pagado">Pagado</MenuItem>
									<MenuItem value="completado">Completado</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<Typography sx={{ fontSize: '0.875rem', mb: 0.75, fontWeight: 500 }}>
								Rango de fechas
							</Typography>
							<RangePicker
								value={dates}
								onChange={(value) => setDates(value)}
								placeholder={['Inicio', 'Fin']}
								style={{ width: '100%' }}
								allowClear
							/>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			<Stack spacing={2}>
				{isLoading && [1, 2, 3].map((key) => (
					<Skeleton key={key} variant="rounded" height={140} animation="wave" />
				))}

				{!isLoading && filteredEvents.length === 0 && (
					<Container maxWidth="sm" sx={{ py: 6 }}>
						<Box sx={{ textAlign: 'center' }}>
							<Avatar sx={{ bgcolor: '#6a1b9a', mx: 'auto', mb: 2 }}>
								<EventAvailableIcon />
							</Avatar>
							<Typography variant="h6">No hay eventos para los filtros aplicados</Typography>
						</Box>
					</Container>
				)}

				{!isLoading && filteredEvents.map((item, index) => {
					const event = item?.event || {};
					const client = item?.client || {};
					const reservation = item?.reservation || {};
					const invoice = item?.invoice || {};
					const profile = getEventProfile(item);
					const paymentState = normalizePaymentStatus(event.paymentStatus || invoice.paymentStatus);

					return (
						<Card key={`${event.eventID || index}-${client.clientID || index}`} sx={{ borderRadius: 2 }}>
							<CardContent>
								<Stack spacing={1.4}>
									<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
										<Stack spacing={1.1} sx={{ minWidth: 0, flex: 1 }}>
											<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
												<Avatar sx={{ bgcolor: '#f759ab', width: 34, height: 34 }}>
													<EventAvailableIcon fontSize="small" />
												</Avatar>
												<Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
													{event.eventTitle || 'Evento sin título'}
												</Typography>
											</Stack>

											<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
												<Chip label={normalizeEventTypeLabel(event.eventType)} size="small" color="info" />
												<Chip
													label={`Pago: ${paymentState.toUpperCase()}`}
													size="small"
													color={paymentState === 'pendiente' ? 'warning' : 'success'}
												/>
											</Stack>
										</Stack>

										<Tooltip title="Ver detalles" arrow>
											<IconButton
												onClick={() => navigate(`/events/view-event-details/${event.eventID}`)}
												aria-label="Ver detalles del evento"
												sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' } }}
											>
												<ListAltIcon />
											</IconButton>
										</Tooltip>
									</Stack>

									<Grid container spacing={1.5}>
										<Grid item xs={12} sm={6}>
											<Stack spacing={1}>
												<Typography variant="body2">
													<PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: 'blue' }} />
													<strong>Cliente:</strong> {client.clientName || 'No especificado'}
												</Typography>
												<Typography variant="body2">
													{profile.icon}
													<strong>{profile.title}:</strong> {profile.value}
												</Typography>
												<Typography variant="body2">
													<EventIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: 'green' }} />
													<strong>Inicio:</strong> {formatDate(event.eventDateStart)}
												</Typography>
												<Typography variant="body2">
													<EventIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: 'red' }} />
													<strong>Fin:</strong> {formatDate(event.eventDateEnd)}
												</Typography>
												<Typography variant="body2">
													<LocationOnIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
													<strong>Lugar:</strong> {event.eventLocation || 'No especificado'}
												</Typography>
											</Stack>
										</Grid>

										<Grid item xs={12} sm={6}>
											<Stack spacing={1}>
												{!!event.eventDescription && (
													<Typography variant="body2">
														<DescriptionIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
														<strong>Descripción:</strong> {event.eventDescription}
													</Typography>
												)}
												{!!reservation?.reservationID && (
													<Typography variant="body2">
														<PaymentIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
														<strong>Reservación:</strong> {reservation.comboName || 'Sin combo'}
														{reservation.comboPrice ? ` (${formatCurrency(reservation.comboPrice)})` : ''}
													</Typography>
												)}
												{!!invoice?.invoiceNumber && (
													<Typography variant="body2">
														<ReceiptLongIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
														<strong>Factura:</strong> {invoice.invoiceNumber}
													</Typography>
												)}
											</Stack>
										</Grid>
									</Grid>
								</Stack>
							</CardContent>
						</Card>
					);
				})}
			</Stack>
		</Box>
	);
}

export default ViewEventsList;
